import {
  OverrideProperty,
  OverridePropertyHelper,
} from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdRelativeDateIntervalCondition,
  DateRangeCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  BdIntervalRelative,
  BdIntervalRelativePart,
} from '@bindoc-projects/time-domain-lib';
import {
  BdDateInterval,
  BdTimeSelection,
  BdTimeSelectionCalendric,
  BdTimeSelectionRelative,
} from '@bindoc-projects/time-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_DISMISSAL_DATE_CALENDRIC_CONDITION_ID =
  'calendricDismissalDate';
export const BD_DISMISSAL_DATE_RELATIVE_CONDITION_ID = 'relativeDismissalDate';
export const FilterConditionDismissalDatePluginType: FilterConditionPluginSpecification<
  BdTimeSelection,
  FilterConditionDismissalDateDto,
  | FilterConditionCalendricDismissalDateCondition
  | {
      start: BdIntervalRelativePart;
      duration: BdIntervalRelativePart;
    }
> = {
  type: 'dismissalDate',
  category: 'case',
  displayName: { de: 'Entlass-Datum' },
  sortingIndex: 8,
  occurrence: 'multiple',
  fromObject(
    obj: FilterConditionDismissalDateDto,
  ): FilterConditionPluginImpl<BdTimeSelection> {
    return new FilterConditionDismissalDatePlugin(
      BdTimeSelection.fromObject(
        obj.timeSelection as
          | {
              type: 'calendric';
              interval: unknown;
            }
          | {
              type: 'relative';
              intervalRelative: {
                start: BdIntervalRelativePart;
                duration: BdIntervalRelativePart;
              };
            },
      ),
    );
  },
  fromCondition(
    jsonObj:
      | FilterConditionCalendricDismissalDateCondition
      | {
          start: BdIntervalRelativePart;
          duration: BdIntervalRelativePart;
        },
  ): FilterConditionDismissalDatePlugin {
    if (Object.hasOwn(jsonObj, 'from')) {
      return new FilterConditionDismissalDatePlugin(
        new BdTimeSelectionCalendric(
          new BdDateInterval(
            new Date(
              (jsonObj as FilterConditionCalendricDismissalDateCondition).from,
            ),
            new Date(
              (jsonObj as FilterConditionCalendricDismissalDateCondition).to,
            ),
          ),
        ),
      );
    } else {
      return new FilterConditionDismissalDatePlugin(
        BdTimeSelectionRelative.fromObject({
          type: 'relative',
          intervalRelative: jsonObj as {
            start: BdIntervalRelativePart;
            duration: BdIntervalRelativePart;
          },
        }),
      );
    }
  },
  newInstance(): FilterConditionPluginImpl<BdTimeSelection> {
    return new FilterConditionDismissalDatePlugin(
      new BdTimeSelectionCalendric(new BdDateInterval(new Date(), new Date())),
    );
  },
};

export class FilterConditionDismissalDatePlugin extends FilterConditionPluginImpl<
  BdTimeSelection,
  FilterConditionDismissalDateDto
> {
  static override Specification: FilterConditionPluginSpecification<
    BdTimeSelection,
    FilterConditionDismissalDateDto,
    | FilterConditionCalendricDismissalDateCondition
    | {
        start: BdIntervalRelativePart;
        duration: BdIntervalRelativePart;
      }
  > = FilterConditionDismissalDatePluginType;

  constructor(private timeSelection: BdTimeSelection) {
    super(FilterConditionDismissalDatePluginType.type);
  }

  override hasRelativeTimeSelection(): boolean {
    return this.timeSelection.type === 'relative';
  }

  toCondition(): BdCondition {
    switch (this.timeSelection.type) {
      case 'relative': {
        const relativeTimeSelection = this
          .timeSelection as BdTimeSelectionRelative;
        return new FilterConditionRelativeDismissalDateCondition(
          relativeTimeSelection.intervalRelative,
        );
      }
      default: {
        const calendricTimeSelection = this
          .timeSelection as BdTimeSelectionCalendric;
        return new FilterConditionCalendricDismissalDateCondition(
          calendricTimeSelection.interval,
        );
      }
    }
  }

  toJSON(): FilterConditionDismissalDateDto | null {
    return {
      type: this.type,
      timeSelection: this.timeSelection.toJSON(),
    };
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items: string[] = [];
    switch (this.timeSelection.type) {
      case 'calendric': {
        const calendric = this.timeSelection as BdTimeSelectionCalendric;
        items.push(calendric.interval.toCompactDateString());
        break;
      }
      case 'relative': {
        const relative = this.timeSelection as BdTimeSelectionRelative;
        items.push(relative.intervalRelative.toDisplayString());
        break;
      }
    }
    return { title, items };
  }

  toText(): string | null {
    return 'Entlass-Datum';
  }

  toValue(): BdTimeSelection {
    return this.timeSelection;
  }

  override getOverrideProperties(
    tileId: string,
  ): OverrideProperty[] {
    return [
      new OverrideProperty<BdTimeSelection>(
        tileId,
        'dismissalDate',
        this.timeSelection,
        this.timeSelection,
        'possible',
        undefined,
        (a, b) => a.equals(b),
        (cloneValue) => cloneValue.clone(),
      ),
    ];
  }

  override overrideClone(
    override: OverrideProperty[],
  ): FilterConditionDismissalDatePlugin {
    const _property = OverridePropertyHelper.overrideProperty<BdTimeSelection>(
      this.timeSelection,
      'dismissalDate',
      override,
      (value) => value.clone(),
    );

    if (_property) {
      return new FilterConditionDismissalDatePlugin(_property);
    } else {
      return null;
    }
  }

  override equals(other: FilterConditionDismissalDatePlugin): boolean {
    return (
      super.equals(other) && this.timeSelection.equals(other.timeSelection)
    );
  }

  override valid(): boolean {
    return super.valid() && this.timeSelection.valid();
  }

  override validationToText(): string {
    if (this.timeSelection && this.timeSelection.valid()) {
      return '';
    } else {
      return 'Einen gültigen Zeitraum auswählen';
    }
  }

  clone(): FilterConditionDismissalDatePlugin {
    return new FilterConditionDismissalDatePlugin(this.timeSelection.clone());
  }
}

export class FilterConditionCalendricDismissalDateCondition extends DateRangeCondition {
  constructor(interval: BdDateInterval) {
    super(BD_DISMISSAL_DATE_CALENDRIC_CONDITION_ID, interval);
  }
}

export class FilterConditionRelativeDismissalDateCondition extends BdRelativeDateIntervalCondition {
  constructor(intervalRelative: BdIntervalRelative) {
    super(BD_DISMISSAL_DATE_RELATIVE_CONDITION_ID, intervalRelative);
  }
}

interface FilterConditionDismissalDateDto {
  type: string;
  timeSelection: unknown;
}
